import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Flag, Share2, MessageCircle, Send, ListPlus, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { usePlaylistDialog } from "@/components/Collections";
import { toast } from "sonner";
import { api, errMsg, mediaUrl, API_BASE } from "@/lib/api";
import { hubByKey, applyHubGlow, fmtNum, fmtTime } from "@/lib/hubs";
import { usePlayer } from "@/store/player";
import { useAuth } from "@/context/AuthContext";
import { Waveform } from "@/components/Waveform";
import { PlayButton, SocialToggle, useIsCurrent } from "@/components/TrackItems";
import { ArtistBadge } from "@/components/ArtistBadge";
import { DmcaDialog } from "@/components/DmcaDialog";

export default function TrackPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [text, setText] = useState("");
  const [dmca, setDmca] = useState(false);
  const [delC, setDelC] = useState(null);
  const isCur = useIsCurrent(id);
  const time = usePlayer((s) => (isCur ? s.currentTime : 0));
  const pdur = usePlayer((s) => (isCur ? s.duration : 0));
  useEffect(() => {
    api.get(`/tracks/${id}`).then((r) => {
      setT(r.data);
      document.title = `${r.data.title} — ${r.data.artist.name} | Soundra`;
      applyHubGlow(hubByKey(r.data.genres[0]));
    }).catch(() => setT(false));
    return () => applyHubGlow(null);
  }, [id]);
  useEffect(() => {
    if (t && isCur) usePlayer.setState((s) => ({ queue: s.queue.map((q, i) => (i === s.index ? { ...q, comments: t.comments } : q)) }));
  }, [t, isCur]);
  if (t === false) return <div className="py-24 text-center text-zinc-400" data-testid="track-not-found">This track is unavailable.</div>;
  if (!t) return <div className="h-80 rounded-3xl bg-white/5 animate-pulse" />;
  const dur = pdur || t.duration;
  const seek = (f) => (isCur ? usePlayer.getState().seek(f * dur) : usePlayer.getState().playTrack(t, [t]));
  const canDelete = (c) => user && (c.user_id === user.user_id || t.artist.user_id === user.user_id || user.role === "admin");
  const deleteComment = async () => {
    const c = delC;
    setDelC(null);
    try {
      await api.delete(`/tracks/${id}/comments/${c.comment_id}`);
      setT((cur) => ({ ...cur, comments: cur.comments.filter((x) => x.comment_id !== c.comment_id) }));
      toast.success("Comment deleted");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const post = async () => {
    if (!user) return toast.error("Sign in to comment");
    try {
      const { data } = await api.post(`/tracks/${id}/comments`, { text, at_seconds: time });
      setT({ ...t, comments: [...t.comments, data].sort((a, b) => a.at_seconds - b.at_seconds) });
      setText("");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="track-page" className="reveal">
      <div className="grid md:grid-cols-[320px_1fr] gap-8 items-end">
        <div className="aspect-square rounded-3xl overflow-hidden bg-zinc-800 shadow-2xl ring-1 ring-white/10">{t.cover_url && <img src={mediaUrl(t.cover_url)} alt={t.title} className="w-full h-full object-cover" />}</div>
        <div>
          <div className="flex gap-2">{t.genres.map((g) => <span key={g} className="eyebrow" style={{ color: hubByKey(g)?.color }}>{hubByKey(g)?.name}</span>)}{t.subtag && <span className="eyebrow">· {t.subtag}</span>}</div>
          <h1 data-testid="track-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-2">{t.title}</h1>
          <Link to={`/artist/${t.artist.slug}`} className="inline-flex items-center gap-2 mt-3 text-lg text-zinc-300 hover:text-white">
            <img src={mediaUrl(t.artist.avatar)} alt="" className="w-8 h-8 rounded-full object-cover" /> {t.artist.name} <ArtistBadge badge={t.artist.badge} />
          </Link>
          <div className="flex items-center gap-5 mt-6">
            <PlayButton track={t} list={[t]} size={60} testId="track-page-play" />
            <SocialToggle track={t} kind="like" initial={t.liked} />
            <SocialToggle track={t} kind="repost" initial={t.reposted} />
            <span className="text-xs font-mono text-zinc-400">{fmtNum(t.streams)} plays</span>
            <button data-testid="track-add-to-playlist" onClick={() => usePlaylistDialog.getState().open(t)} className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-white"><ListPlus size={16} /> Add to playlist</button>
            <button data-testid="track-share-button" onClick={() => { navigator.clipboard?.writeText(`${API_BASE}/api/share/track/${t.track_id}`); toast.success("Share link copied"); }} className="text-zinc-400 hover:text-white"><Share2 size={16} /></button>
            <button data-testid="track-dmca-button" onClick={() => setDmca(true)} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-red-400"><Flag size={14} /> Report copyright</button>
          </div>
        </div>
      </div>
      <div className="surface p-6 mt-10">
        <div className="flex justify-between font-mono text-xs text-zinc-500 mb-3"><span>{fmtTime(time)}</span><span>{fmtTime(dur)}</span></div>
        <Waveform peaks={t.peaks} bars={200} height={96} duration={dur} progress={dur ? time / dur : 0} onSeek={seek} comments={t.comments} testId="track-page-waveform" />
        <div className="flex gap-3 mt-8">
          <input data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && text.trim() && post()} maxLength={300}
            placeholder={`Comment at ${fmtTime(time)}…`} className="flex-1 h-11 px-4 rounded-full bg-white/5 border border-white/10 focus:border-[var(--hub)] outline-none text-sm" />
          <button data-testid="comment-submit-button" disabled={!text.trim()} onClick={post} className="w-11 h-11 rounded-full bg-[var(--hub)] disabled:opacity-40 flex items-center justify-center"><Send size={16} /></button>
        </div>
      </div>
      {t.description && <p className="text-zinc-400 mt-6 max-w-3xl">{t.description}</p>}
      {t.credits && <p data-testid="track-credits" className="text-xs text-zinc-500 mt-2 max-w-3xl"><span className="eyebrow mr-2">Credits</span>{t.credits}</p>}
      <div className="mt-8" data-testid="comments-list">
        <h2 className="font-display text-xl font-bold flex items-center gap-2 mb-4"><MessageCircle size={18} /> {t.comments.length} timed comments</h2>
        <div className="space-y-2">{t.comments.map((c) => (
          <div key={c.comment_id} data-testid={`comment-${c.comment_id}`} className="group flex gap-3 items-start p-3 rounded-xl hover:bg-white/[0.03]">
            <button onClick={() => seek(c.at_seconds / dur)} className="font-mono text-xs text-[var(--hub)] w-12 pt-0.5">{fmtTime(c.at_seconds)}</button>
            <div className="flex-1 min-w-0"><span className="text-sm font-semibold">{c.user_name}</span><p className="text-sm text-zinc-300 break-words">{c.text}</p></div>
            {canDelete(c) && (
              <button data-testid={`comment-delete-${c.comment_id}`} onClick={() => setDelC(c)} aria-label="Delete comment"
                className="shrink-0 p-1.5 rounded-full text-zinc-500 hover:text-red-400 hover:bg-white/5 opacity-60 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity"><Trash2 size={14} /></button>
            )}
          </div>))}
        </div>
      </div>
      <DmcaDialog open={dmca} onOpenChange={setDmca} track={t} />
      <AlertDialog open={!!delC} onOpenChange={(o) => !o && setDelC(null)}>
        <AlertDialogContent data-testid="comment-delete-dialog" className="bg-[#12121A] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">Are you sure you want to delete this comment?</AlertDialogTitle>
            <AlertDialogDescription className="break-words">“{delC?.text}”</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="comment-delete-cancel" className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="comment-delete-confirm" onClick={deleteComment} className="rounded-full bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
